import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import { Plus, Save, Trash2 } from "lucide-react";
import { Card, Btn, Input } from "@/components/admin/ui";
import { API } from "@/config";
import { fmtErr } from "@/context/AuthProvider";
import { ApprovedLocations } from "./ApprovedLocations";

const ZoneMapEditor = ({ zone, onPolygonChange }) => {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  useEffect(() => {
    if (!mapEl.current || mapRef.current) return;
    const map = L.map(mapEl.current).setView([28.385, -81.563], 11);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap", maxZoom: 19 }).addTo(map);
    const drawn = new L.FeatureGroup();
    map.addLayer(drawn);
    layerRef.current = drawn;
    if (zone.polygon && zone.polygon.length >= 3) {
      const latlngs = zone.polygon.map(([lng, lat]) => [lat, lng]);
      const p = L.polygon(latlngs, { color: "#FFD166", weight: 2, fillColor: "#D97B37", fillOpacity: 0.25 });
      drawn.addLayer(p);
      map.fitBounds(p.getBounds(), { padding: [20, 20] });
    }
    const drawControl = new L.Control.Draw({
      draw: { polygon: { showArea: true, allowIntersection: false, shapeOptions: { color: "#FFD166" } }, polyline: false, rectangle: true, circle: false, marker: false, circlemarker: false },
      edit: { featureGroup: drawn, remove: true },
    });
    map.addControl(drawControl);
    const emit = () => {
      const layers = drawn.getLayers();
      if (!layers.length) return onPolygonChange([]);
      const l = layers[layers.length - 1];
      const latlngs = l.getLatLngs()[0] || l.getLatLngs();
      const coords = latlngs.map(ll => [ll.lng, ll.lat]);
      if (coords.length >= 3 && (coords[0][0] !== coords[coords.length - 1][0] || coords[0][1] !== coords[coords.length - 1][1])) coords.push(coords[0]);
      onPolygonChange(coords);
    };
    map.on(L.Draw.Event.CREATED, (e) => { drawn.clearLayers(); drawn.addLayer(e.layer); emit(); });
    map.on(L.Draw.Event.EDITED, emit);
    map.on(L.Draw.Event.DELETED, emit);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, [zone.id]); // eslint-disable-line
  return <div ref={mapEl} className="w-full h-[420px] rounded-xl overflow-hidden border border-[#FFD166]/25" data-testid={`zone-map-${zone.id}`} />;
};

export const Zones = () => {
  const [zones, setZones] = useState([]);
  const [drafts, setDrafts] = useState({});
  const load = useCallback(() => axios.get(`${API}/admin/zones`).then(r => setZones(r.data.zones)), []);
  useEffect(() => { load(); }, [load]);
  const setDraft = (id, patch) => setDrafts(d => ({ ...d, [id]: { ...(d[id] || {}), ...patch } }));
  const save = async (z) => {
    const patch = drafts[z.id] || {};
    try {
      await axios.patch(`${API}/admin/zones/${z.id}`, patch);
      toast.success("Zone saved"); setDrafts(d => ({ ...d, [z.id]: undefined })); load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const addZone = async () => {
    const name = window.prompt("Zone name (e.g. 'Universal / Bonnet Creek')");
    if (!name) return;
    await axios.post(`${API}/admin/zones`, { area: name, resorts: [], delivery_fee: 0, active: true, polygon: [], requires_meet: true });
    load();
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-4xl">Delivery Zones</h1>
          <p className="text-[#B9C8D5] text-sm mt-1 max-w-2xl">Draw the exact area you deliver to on the map. Customers whose address falls outside every zone will be told you don't deliver there. Disney policy requires an in-person meet for delivery and pickup.</p>
        </div>
        <Btn onClick={addZone} data-testid="zone-add-btn"><Plus className="w-3.5 h-3.5" /> New Zone</Btn>
      </div>
      <div className="space-y-6">
        {zones.map(z => (
          <Card key={z.id} className="p-5" data-testid={`zone-row-${z.id}`}>
            <div className="grid md:grid-cols-[1fr_auto] gap-4 items-start">
              <div>
                <Input defaultValue={z.area} onChange={e => setDraft(z.id, { area: e.target.value })} data-testid={`zone-name-${z.id}`} />
                <div className="mt-3 flex flex-wrap gap-3 items-center text-sm">
                  <label className="text-[#B9C8D5]">Delivery fee $</label>
                  <Input type="number" step="0.01" defaultValue={z.delivery_fee} onChange={e => setDraft(z.id, { delivery_fee: Number(e.target.value) })} className="w-28" data-testid={`zone-fee-${z.id}`} />
                  <label className="flex items-center gap-2"><input type="checkbox" defaultChecked={z.active} onChange={e => setDraft(z.id, { active: e.target.checked })} data-testid={`zone-active-${z.id}`} /><span className="text-[#B9C8D5]">Active</span></label>
                </div>
                <textarea defaultValue={(z.resorts || []).join(", ")} onChange={e => setDraft(z.id, { resorts: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} rows="2" className="mt-3 w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg" placeholder="Landmark / resort names, comma-separated" />
              </div>
              <div className="flex flex-col gap-2">
                <Btn onClick={() => save(z)} data-testid={`zone-save-${z.id}`}><Save className="w-3.5 h-3.5" /> Save</Btn>
                <Btn variant="danger" onClick={async () => { if (window.confirm("Delete this zone?")) { await axios.delete(`${API}/admin/zones/${z.id}`); load(); } }}><Trash2 className="w-3.5 h-3.5" /></Btn>
              </div>
            </div>
            <div className="mt-4">
              <ZoneMapEditor zone={z} onPolygonChange={(poly) => setDraft(z.id, { polygon: poly })} />
              <p className="text-xs text-[#B9C8D5] mt-2">Use the polygon or rectangle tool on the map, then click Save.</p>
            </div>
          </Card>
        ))}
      </div>
      <ApprovedLocations zones={zones} />
    </div>
  );
};
